"use client";

import { useEffect, useState } from "react";

type Mode = "A" | "B" | "C";

type SelfServeSettings = {
  selfServeMode: Mode;
  autoApprove: boolean;
  source: string;
};

type State = {
  data: SelfServeSettings;
  loading: boolean;
  error: string | null;
};

const ENV_MODE_KEY = process.env.NEXT_PUBLIC_SELF_SERVE_MODE;
const ENV_MODE = normalizeMode(ENV_MODE_KEY, "A");
const DEFAULT_STATE: State = {
  data: {
    selfServeMode: ENV_MODE,
    autoApprove: false,
    source: "env",
  },
  loading: true,
  error: null,
};

let cachedState: State | null = null;
let inflight: Promise<SelfServeSettings> | null = null;

function normalizeMode(input: unknown, fallback: Mode): Mode {
  const raw = typeof input === "string" ? input.toUpperCase() : "";
  if (raw === "A" || raw === "B" || raw === "C") return raw;
  return fallback;
}

async function fetchSettings(): Promise<SelfServeSettings> {
  if (!inflight) {
    inflight = fetch("/api/self-serve", { cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        return {
          selfServeMode: normalizeMode(json?.selfServeMode, ENV_MODE),
          autoApprove: Boolean(json?.autoApprove),
          source: typeof json?.source === "string" ? json.source : "env",
        } satisfies SelfServeSettings;
      })
      .catch((error) => {
        console.error(
          "[useSelfServeSettings] failed to fetch settings:",
          error,
        );
        return {
          selfServeMode: ENV_MODE,
          autoApprove: false,
          source: "env",
        } satisfies SelfServeSettings;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useSelfServeSettings() {
  const [state, setState] = useState<State>(() => cachedState ?? DEFAULT_STATE);

  useEffect(() => {
    let active = true;
    (async () => {
      const settings = await fetchSettings();
      if (!active) return;
      const nextState: State = { data: settings, loading: false, error: null };
      cachedState = nextState;
      setState(nextState);
    })();

    return () => {
      active = false;
    };
  }, []);

  const isSelfServeOpen =
    state.data.selfServeMode === "A" || state.data.selfServeMode === "C";

  return {
    ...state,
    isSelfServeOpen,
  };
}
